/*
 * Copyright (c) 2025 MTa Learning. All Rights Reserved.
 *
 * MTa Learning proprietary information: the enclosed materials contain proprietary information of MTa Learning and
 * shall not be disclosed in whole or in any part to any third party or used by any person for any purpose, without
 * written consent of MTa Learning. Duplication of any portion of these materials shall include this notice.
 */
;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:mta-learning":true});}catch(e){}}();import type { PricingZone } from '@/utils/pricing-zones'
import { getPricingZoneByCountryCode } from '@/utils/pricing-zones'

/** A named region combining a language, URL slug, hreflang code, and pricing zone. */
type Region = {
  /** URL slug used in the pathname, e.g. 'en-gb', 'es'. */
  slug: string
  /** Language code: 'en' or 'es'. */
  lang: 'en' | 'es'
  /** Human-readable label for the region selector dropdown. */
  label: string
  /** hreflang attribute value for SEO tags, e.g. 'en-GB', 'es'. */
  hreflangCode: string
  /** Additional country codes served by the same regional URL. */
  hreflangAliases?: readonly string[]
  /** The pricing zone that determines currency and pricing for this region. */
  pricingZone: PricingZone
}

/** The 7 named regions used across the site. */
const REGIONS: readonly Region[] = [
  {
    slug: 'en-gb',
    lang: 'en',
    label: 'Europe, UK & Africa',
    hreflangCode: 'en',
    hreflangAliases: ['en-GB'],
    pricingZone: 'uk',
  },
  {
    slug: 'en-mena',
    lang: 'en',
    label: 'Middle East & North Africa',
    hreflangCode: 'en-AE',
    hreflangAliases: ['en-SA', 'en-QA', 'en-EG'],
    pricingZone: 'mena',
  },
  {
    slug: 'en-asia',
    lang: 'en',
    label: 'Asia',
    hreflangCode: 'en-SG',
    hreflangAliases: ['en-IN', 'en-MY', 'en-HK'],
    pricingZone: 'asia',
  },
  {
    slug: 'en-aus',
    lang: 'en',
    label: 'Australia & Oceania',
    hreflangCode: 'en-AU',
    hreflangAliases: ['en-NZ'],
    pricingZone: 'australasia',
  },
  {
    slug: 'en-us',
    lang: 'en',
    label: 'North America',
    hreflangCode: 'en-US',
    hreflangAliases: ['en-CA'],
    pricingZone: 'north_america',
  },
  {
    slug: 'es-es',
    lang: 'es',
    label: 'España / Europa',
    hreflangCode: 'es-ES',
    pricingZone: 'eu',
  },
  {
    slug: 'es',
    lang: 'es',
    label: 'Latinoamérica',
    hreflangCode: 'es',
    pricingZone: 'latam',
  },
] as const

/** Set of valid region slugs for fast lookup. */
const REGION_SLUGS = new Set(REGIONS.map((r) => r.slug))

/** The default region used for x-default hreflang and fallback. */
const DEFAULT_REGION = REGIONS[0] // en-gb

/** Lookup a region by its URL slug. */
const getRegionBySlug = (slug: string): Region | undefined => REGIONS.find((r) => r.slug === slug)

/** Resolve a public region slug to the language used by translations and Storyblok. */
const getLanguageForRegionSlug = (slug: string): Region['lang'] => getRegionBySlug(slug)?.lang ?? DEFAULT_REGION.lang

/**
 * Maps a pricing zone to the default English region for that zone.
 * Used when geo-detecting a user's country → region.
 */
const ZONE_TO_EN_REGION: Record<PricingZone, Region> = {
  uk: REGIONS[0], // en-gb
  eu: REGIONS[0], // en-gb (EU rolled into en-gb)
  subsahara: REGIONS[0], // en-gb (Africa rolled into en-gb)
  mena: REGIONS[1], // en-mena
  asia: REGIONS[2], // en-asia
  australasia: REGIONS[3], // en-aus
  north_america: REGIONS[4], // en-us
  latam: REGIONS[4], // en-us (English latam users → Americas)
}

/** Maps a pricing zone to the Spanish region for that zone. */
const ZONE_TO_ES_REGION: Record<PricingZone, Region> = {
  uk: REGIONS[5], // es-es
  eu: REGIONS[5], // es-es
  subsahara: REGIONS[5], // es-es
  mena: REGIONS[5], // es-es
  asia: REGIONS[5], // es-es
  australasia: REGIONS[5], // es-es
  north_america: REGIONS[6], // es
  latam: REGIONS[6], // es
}

/**
 * Determine the appropriate region for a given country code and language.
 * Uses the pricing zone mapping to find the region.
 *
 * @param countryCode - ISO 3166-1 alpha-2 country code (e.g. 'GB', 'US')
 * @param lang - preferred language, defaults to 'en'
 * @returns the matching Region, or DEFAULT_REGION if no zone match
 */
const getRegionForCountry = (countryCode: string, lang: 'en' | 'es' = 'en'): Region => {
  const zone = getPricingZoneByCountryCode(countryCode)
  if (!zone) return lang === 'es' ? REGIONS[5] : DEFAULT_REGION
  return lang === 'es' ? ZONE_TO_ES_REGION[zone] : ZONE_TO_EN_REGION[zone]
}

/**
 * Get the language-sibling region for language switching.
 * en-gb ↔ es-es, en-us ↔ es. All other English regions → es-es.
 */
const getLanguageSibling = (region: Region): Region => {
  if (region.lang === 'en') {
    if (region.slug === 'en-us') return REGIONS[6] // es
    return REGIONS[5] // es-es
  }
  // Spanish → English
  if (region.slug === 'es') return REGIONS[4] // en-us
  return REGIONS[0] // en-gb
}

/** Maps a pricing zone to its representative ISO country code (used for derived country state). */
const ZONE_REPRESENTATIVE_COUNTRY: Record<PricingZone, string> = {
  uk: 'GB',
  north_america: 'US',
  eu: 'DE',
  australasia: 'AU',
  mena: 'AE',
  subsahara: 'ZA',
  asia: 'JP',
  latam: 'BR',
}

/** Get the representative country code for a region's pricing zone. */
const getRepresentativeCountry = (region: Region): string => ZONE_REPRESENTATIVE_COUNTRY[region.pricingZone]

/**
 * Maps old region slugs to their new equivalents.
 * Used by middleware to handle old cookie values and URL redirects.
 */
const LEGACY_SLUG_MAP: Record<string, string> = {
  'en-eu': 'en-gb',
  'es-latam': 'es',
}

/** Resolve a slug that might be a legacy value to its current equivalent. */
const resolveLegacySlug = (slug: string): string => LEGACY_SLUG_MAP[slug] ?? slug

export {
  REGIONS,
  REGION_SLUGS,
  DEFAULT_REGION,
  getRegionBySlug,
  getLanguageForRegionSlug,
  getRegionForCountry,
  getLanguageSibling,
  getRepresentativeCountry,
  LEGACY_SLUG_MAP,
  resolveLegacySlug,
}
export type { Region }
